import { useState, useLayoutEffect, useMemo, useCallback } from "react";
import { ScrollView, Pressable } from "react-native";
import { useRouter, useLocalSearchParams, useNavigation } from "expo-router";
import { View, Text, ScreenContainer, FormInput, phoneFormatter } from "@src/components";
import { useUnistyles } from "react-native-unistyles";
import { smsContactSchema } from "@src/features/contacts/schemas";
import { useSmsContactSubmit } from "@src/features/contacts/hooks";
import { ContactCategory } from "@src/features/contacts/types";
import { useCameraContacts } from "@src/services/api/hooks";
import { showErrorToast, showSuccessToast } from "@src/utils/toast";
import { Icon } from "@src/components/Icon";
import { useHeaderBackButton } from "@src/hooks/useHeaderBackButton";
import { useRegionConfig } from "@src/hooks/useRegionConfig";
import { useTenantId } from "@src/features/tenants";

export default function AddSmsContact() {
  const router = useRouter();
  const navigation = useNavigation();
  const { theme } = useUnistyles();
  const tenantId = useTenantId();
  const { phoneCountryCode } = useRegionConfig();
  const { category, siteId, cameraKey } = useLocalSearchParams<{
    category: ContactCategory;
    siteId?: string;
    cameraKey?: string;
  }>();
  const fallbackRoute = useMemo(() => {
    if (!category || !siteId || !cameraKey || !tenantId) return undefined;
    return `/tenants/${encodeURIComponent(tenantId)}/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(
      cameraKey,
    )}/activity-notification`;
  }, [category, siteId, cameraKey, tenantId]);
  const { headerLeft } = useHeaderBackButton(fallbackRoute);

  const hasCameraContext = Boolean(siteId && cameraKey);
  const { data: cameraContacts } = useCameraContacts(siteId, cameraKey);

  const [name, setName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [errors, setErrors] = useState<{ name?: string; phone_number?: string }>({});

  const { submitSms, isSubmitting } = useSmsContactSubmit({
    siteId,
    cameraKey,
    cameraContacts: cameraContacts ?? null,
  });

  const handleValidateAndAdd = useCallback(() => {
    try {
      const validated = smsContactSchema.parse({
        name: name.trim(),
        phone_number: phoneNumber.trim(),
      });

      submitSms(
        {
          name: validated.name,
          phone_number: validated.phone_number,
        },
        {
          onSuccess: () => {
            router.back();
            showSuccessToast({ message: "Contact added successfully" });
          },
          onError: (error: { message: string }) => {
            showErrorToast({ error });
          },
        },
      );
    } catch (err: unknown) {
      const newErrors: { name?: string; phone_number?: string } = {};
      const errorMessages: string[] = [];

      const issues = (err as { issues: { path: string[]; message: string }[] }).issues || [];

      issues.forEach((error: { path: string[]; message: string }) => {
        const field = error.path[0];
        newErrors[field as keyof typeof newErrors] = error.message;
        errorMessages.push(error.message);
      });

      setErrors(newErrors);

      const alertMessage = errorMessages.join("\n");
      showErrorToast({ message: "Invalid contact information", description: alertMessage });
    }
  }, [name, phoneNumber, router, submitSms]);

  const handleChangeName = (text: string) => {
    setName(text);
    setErrors((prev) => ({ ...prev, name: undefined }));
  };

  const handleChangePhoneNumber = (text: string) => {
    setPhoneNumber(text);
    setErrors((prev) => ({ ...prev, phone_number: undefined }));
  };

  const isFormValid = name.trim().length > 0 && phoneNumber.trim().length > 0;

  const headerRight = useMemo(
    () => (
      <Pressable
        onPress={handleValidateAndAdd}
        disabled={!isFormValid || isSubmitting}
        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        style={{ paddingHorizontal: 16, paddingVertical: 4 }}
      >
        {isSubmitting ?
          <Text textStyle="md/semibold" color="textMuted">
            Adding...
          </Text>
        : <Text textStyle="md/semibold" color={isFormValid ? "navy" : "textMuted"}>
            Add
          </Text>
        }
      </Pressable>
    ),
    [isFormValid, handleValidateAndAdd, isSubmitting],
  );

  useLayoutEffect(() => {
    navigation.setOptions({
      title: "Add SMS Contact",
      headerBackButtonDisplayMode: "minimal",
      headerRight: () => headerRight,
      headerLeft,
    });
  }, [navigation, headerRight, headerLeft]);

  return (
    <ScreenContainer edges={[]}>
      <ScrollView style={{ flex: 1 }} contentContainerStyle={{ flexGrow: 1, paddingBottom: 20 }}>
        <View px="5xl" py="4xl">
          <View mb="3xl">
            <FormInput
              value={name}
              onChangeText={handleChangeName}
              placeholder="Contact name"
              autoCapitalize="words"
              returnKeyType="next"
              error={errors.name}
              iconRight={<Icon name="userOutline" size={20} color={theme.colors.icon} />}
            />
          </View>

          <View mb="3xl">
            <FormInput
              value={phoneNumber}
              onChangeText={handleChangePhoneNumber}
              formatter={phoneFormatter}
              formatterConfig={{ country: phoneCountryCode }}
              placeholder="Phone number"
              returnKeyType="done"
              onSubmitEditing={handleValidateAndAdd}
              error={errors.phone_number}
              iconRight={<Icon name="call" size={20} color={theme.colors.icon} />}
            />
          </View>

          <Text textStyle="sm/regular" color="textMuted">
            SMS messages may incur additional charges from your mobile carrier
          </Text>
        </View>
      </ScrollView>
    </ScreenContainer>
  );
}
